import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { toast } from 'sonner';
import { 
  Layers, Package, Clock, CheckCircle2, AlertCircle, 
  Eye, Play, Printer, ChevronDown, ChevronRight, Scale,
  Users, Settings, FileText, RefreshCw, Plus, Trash2, Calendar
} from 'lucide-react';
import { format } from 'date-fns';
import { formatWeight, formatPercentage, formatBomQty } from '@/utils/formatters';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  issued: 'bg-blue-100 text-blue-800 border-blue-200',
  in_progress: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  completed: 'bg-green-100 text-green-800 border-green-200'
};

const statusLabels = {
  issued: 'Issued',
  in_progress: 'In Progress',
  completed: 'Completed'
};

const materialStatusConfig = {
  ready: { color: 'bg-green-500', label: 'Ready', textColor: 'text-green-700' },
  partial: { color: 'bg-amber-500', label: 'Partial', textColor: 'text-amber-700' },
  none: { color: 'bg-red-500', label: 'None', textColor: 'text-red-700' }
};

export default function PooledJobsPage() {
  const { user, getAuthHeader } = useAuth();
  const [pooledJobs, setPooledJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [expandedJob, setExpandedJob] = useState(null);
  const [receiveDialog, setReceiveDialog] = useState({ open: false, job: null });
  const [receiveForm, setReceiveForm] = useState({});

  // Create Pooled Job state
  const [poolableData, setPoolableData] = useState({ distinct_processes: [], poolable_items: [] });
  const [selectedProcessName, setSelectedProcessName] = useState('');
  const [selectedItems, setSelectedItems] = useState({});
  const [loadingPoolable, setLoadingPoolable] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    workers: [{ employee_id: '', employee_name: '', role: 'operator' }],
    machine: '',
    drawing_number: '',
    remarks: ''
  });
  const [employees, setEmployees] = useState([]);
  
  // Track first selected item's materials for validation
  const [firstSelectedMaterials, setFirstSelectedMaterials] = useState(null);
  
  // Track cumulative material requirements across all selected items
  const [cumulativeMaterials, setCumulativeMaterials] = useState({});
  // Format: { "item-id-1": { item_name: "Steel Sheet", required_qty: 80, available_qty: 100 }, ... }

  useEffect(() => {
    fetchPooledJobs();
    fetchPoolableProcesses();
    fetchEmployees();
  }, []);

  const fetchPooledJobs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/pooled-jobs`, { headers: getAuthHeader() });
      setPooledJobs(response.data);
    } catch (error) {
      console.error('Error fetching pooled jobs:', error);
      toast.error('Failed to load pooled jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchPoolableProcesses = async (processName = null) => {
    setLoadingPoolable(true);
    try {
      const params = processName ? `?process_name=${encodeURIComponent(processName)}` : '';
      const response = await axios.get(`${API_URL}/pooled-jobs/poolable-processes${params}`, { headers: getAuthHeader() });
      setPoolableData(response.data);
    } catch (error) {
      console.error('Error fetching poolable processes:', error);
      toast.error('Failed to load poolable processes');
    } finally {
      setLoadingPoolable(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/employees`, { headers: getAuthHeader() });
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const activeJobs = pooledJobs.filter(j => j.status === 'issued' || j.status === 'in_progress');
  const completedJobs = pooledJobs.filter(j => j.status === 'completed');

  // Material status priority for sorting (lower = higher priority)
  const materialStatusPriority = { ready: 0, partial: 1, none: 2 };

  // Filter and sort poolable items by selected process
  const filteredPoolableItems = React.useMemo(() => {
    if (!selectedProcessName) return [];
    
    const items = poolableData.poolable_items.filter(item => item.process_name === selectedProcessName);
    
    // Get the first selected item's materials for comparison
    const selectedMaterialsKey = firstSelectedMaterials?.items?.slice().sort().join(',') || null;
    
    return items.sort((a, b) => {
      // If there's a selection, sort matching materials to the top
      if (selectedMaterialsKey) {
        const aMaterialsKey = (a.material_items || []).slice().sort().join(',');
        const bMaterialsKey = (b.material_items || []).slice().sort().join(',');
        const aMatches = aMaterialsKey === selectedMaterialsKey;
        const bMatches = bMaterialsKey === selectedMaterialsKey;
        
        // Matching materials come first
        if (aMatches && !bMatches) return -1;
        if (!aMatches && bMatches) return 1;
      }
      
      // Within same match group, sort by material status (Ready > Partial > None)
      const statusA = materialStatusPriority[a.material_status] ?? 2;
      const statusB = materialStatusPriority[b.material_status] ?? 2;
      if (statusA !== statusB) return statusA - statusB;
      
      // Then sort by delivery date (earliest first, null/undefined last)
      const dateA = a.delivery_date ? new Date(a.delivery_date) : null;
      const dateB = b.delivery_date ? new Date(b.delivery_date) : null;
      if (dateA && dateB) return dateA - dateB;
      if (dateA && !dateB) return -1;
      if (!dateA && dateB) return 1;
      return 0;
    });
  }, [selectedProcessName, poolableData.poolable_items, firstSelectedMaterials]);

  // Calculate selection totals
  const selectedCount = Object.keys(selectedItems).filter(k => selectedItems[k]).length;
  const selectedTotalQty = Object.entries(selectedItems)
    .filter(([_, selected]) => selected)
    .reduce((sum, [key]) => {
      const item = filteredPoolableItems.find(i => `${i.work_order_id}-${i.process_id}` === key);
      return sum + (item?.pending_qty || 0);
    }, 0);

  const handleProcessChange = (processName) => {
    setSelectedProcessName(processName);
    setSelectedItems({});
    setFirstSelectedMaterials(null);  // Reset material tracking
    setCumulativeMaterials({});  // Reset cumulative tracking
    if (processName) {
      fetchPoolableProcesses(processName);
    }
  };

  const handleClearSelection = () => {
    setSelectedItems({});
    setFirstSelectedMaterials(null);
    setCumulativeMaterials({});
  };

  const toggleItemSelection = (item) => {
    const key = `${item.work_order_id}-${item.process_id}`;
    const isCurrentlySelected = selectedItems[key];
    
    setSelectedItems(prev => {
      const newSelection = { ...prev, [key]: !isCurrentlySelected };
      
      // Count how many items are now selected
      const selectedCount = Object.values(newSelection).filter(v => v).length;
      
      // Update first selected materials tracking
      if (!isCurrentlySelected) {
        // We're selecting this item
        if (selectedCount === 1) {
          // This is the first selection - set its materials as reference
          setFirstSelectedMaterials({
            items: item.material_items || [],
            names: item.material_names || [],
            woNumber: item.work_order_number
          });
        }
        
        // Update cumulative materials - add this item's requirements
        setCumulativeMaterials(prevCumulative => {
          const updated = { ...prevCumulative };
          (item.material_requirements || []).forEach(mat => {
            if (updated[mat.item_id]) {
              updated[mat.item_id] = {
                ...updated[mat.item_id],
                required_qty: updated[mat.item_id].required_qty + mat.required_qty
              };
            } else {
              updated[mat.item_id] = {
                item_name: mat.item_name,
                required_qty: mat.required_qty,
                available_qty: mat.available_qty
              };
            }
          });
          return updated;
        });
      } else {
        // We're deselecting this item
        if (selectedCount === 0) {
          // No items selected - clear material tracking
          setFirstSelectedMaterials(null);
          setCumulativeMaterials({});
        } else {
          if (firstSelectedMaterials?.woNumber === item.work_order_number) {
            // We deselected the first item - find a new first item
            const remainingKey = Object.entries(newSelection).find(([k, v]) => v && k !== key)?.[0];
            if (remainingKey) {
              const remainingItem = filteredPoolableItems.find(i => `${i.work_order_id}-${i.process_id}` === remainingKey);
              if (remainingItem) {
                setFirstSelectedMaterials({
                  items: remainingItem.material_items || [],
                  names: remainingItem.material_names || [],
                  woNumber: remainingItem.work_order_number
                });
              }
            }
          }
          
          // Update cumulative materials - subtract this item's requirements
          setCumulativeMaterials(prevCumulative => {
            const updated = { ...prevCumulative };
            (item.material_requirements || []).forEach(mat => {
              if (updated[mat.item_id]) {
                const newQty = updated[mat.item_id].required_qty - mat.required_qty;
                if (newQty <= 0) {
                  delete updated[mat.item_id];
                } else {
                  updated[mat.item_id] = {
                    ...updated[mat.item_id],
                    required_qty: newQty
                  };
                }
              }
            });
            return updated;
          });
        }
      }
      
      return newSelection;
    });
  };
  
  // Check if an item's materials match the first selected item's materials
  const hasMaterialMismatch = (item) => {
    if (!firstSelectedMaterials) return false;
    const itemMaterials = (item.material_items || []).slice().sort().join(',');
    const firstMaterials = (firstSelectedMaterials.items || []).slice().sort().join(',');
    return itemMaterials !== firstMaterials;
  };
  
  // Check if selecting this item would exceed available stock for any material
  const wouldExceedStock = (item) => {
    if (!item.material_requirements?.length) return null;
    
    for (const mat of item.material_requirements) {
      const currentCumulative = cumulativeMaterials[mat.item_id]?.required_qty || 0;
      const wouldBeTotal = currentCumulative + mat.required_qty;
      if (wouldBeTotal > mat.available_qty) {
        return {
          item_name: mat.item_name,
          would_need: wouldBeTotal,
          available: mat.available_qty
        };
      }
    }
    return null;
  };

  const selectAll = () => {
    const newSelection = {};
    const newCumulativeMaterials = {};
    let firstItem = null;
    
    // First pass: find the first item with ready materials to set as reference
    for (const item of filteredPoolableItems) {
      if (item.material_status === 'ready') {
        firstItem = item;
        break;
      }
    }
    
    if (!firstItem) {
      toast.error('No items with ready materials to select');
      return;
    }
    
    const referenceMaterials = (firstItem.material_items || []).slice().sort().join(',');
    
    // Second pass: select items with matching materials, respecting stock limits
    let skippedDueToStock = 0;
    filteredPoolableItems.forEach(item => {
      if (item.material_status === 'ready') {
        const itemMaterials = (item.material_items || []).slice().sort().join(',');
        if (itemMaterials === referenceMaterials) {
          // Check if selecting this item would exceed stock
          let canSelect = true;
          for (const mat of (item.material_requirements || [])) {
            const currentCumulative = newCumulativeMaterials[mat.item_id]?.required_qty || 0;
            const wouldBeTotal = currentCumulative + mat.required_qty;
            if (wouldBeTotal > mat.available_qty) {
              canSelect = false;
              break;
            }
          }
          
          if (canSelect) {
            newSelection[`${item.work_order_id}-${item.process_id}`] = true;
            // Add to cumulative
            (item.material_requirements || []).forEach(mat => {
              if (newCumulativeMaterials[mat.item_id]) {
                newCumulativeMaterials[mat.item_id].required_qty += mat.required_qty;
              } else {
                newCumulativeMaterials[mat.item_id] = {
                  item_name: mat.item_name,
                  required_qty: mat.required_qty,
                  available_qty: mat.available_qty
                };
              }
            });
          } else {
            skippedDueToStock++;
          }
        }
      }
    });
    
    setSelectedItems(newSelection);
    setCumulativeMaterials(newCumulativeMaterials);
    setFirstSelectedMaterials({
      items: firstItem.material_items || [],
      names: firstItem.material_names || [],
      woNumber: firstItem.work_order_number
    });
    
    if (skippedDueToStock > 0) {
      toast.warning(`${skippedDueToStock} item(s) skipped due to insufficient stock for combined requirements`);
    }
  };

  const clearSelection = () => {
    setSelectedItems({});
    setFirstSelectedMaterials(null);
    setCumulativeMaterials({});
  };

  const openCreateDialog = () => {
    setCreateForm({
      workers: [{ employee_id: '', employee_name: '', role: 'operator' }],
      machine: '',
      drawing_number: '',
      remarks: ''
    });
    setCreateDialogOpen(true);
  };

  const addWorker = () => {
    if (createForm.workers.length >= 5) {
      toast.error('Maximum 5 workers allowed');
      return;
    }
    setCreateForm({
      ...createForm,
      workers: [...createForm.workers, { employee_id: '', employee_name: '', role: 'operator' }]
    });
  };

  const removeWorker = (idx) => {
    if (createForm.workers.length <= 1) return;
    setCreateForm({
      ...createForm,
      workers: createForm.workers.filter((_, i) => i !== idx)
    });
  };

  const updateWorker = (idx, field, value) => {
    const updated = [...createForm.workers];
    if (field === 'employee_id') {
      const emp = employees.find(e => e.id === value);
      updated[idx] = { ...updated[idx], employee_id: value, employee_name: emp?.name || '' };
    } else {
      updated[idx] = { ...updated[idx], [field]: value };
    }
    setCreateForm({ ...createForm, workers: updated });
  };

  const handleCreatePooledJob = async () => {
    const selectedKeys = Object.entries(selectedItems).filter(([_, v]) => v).map(([k]) => k);
    if (selectedKeys.length === 0) {
      toast.error('Select at least one process');
      return;
    }

    const processSelections = selectedKeys.map(key => {
      const [work_order_id, process_id] = key.split('-');
      return { work_order_id, process_id };
    });

    const validWorkers = createForm.workers.filter(w => w.employee_id);

    try {
      const response = await axios.post(`${API_URL}/pooled-jobs`, {
        process_selections: processSelections,
        workers: validWorkers,
        machine: createForm.machine,
        drawing_number: createForm.drawing_number,
        remarks: createForm.remarks
      }, { headers: getAuthHeader() });

      toast.success(`Pooled Job ${response.data.pooled_job_number} created!`);
      setCreateDialogOpen(false);
      setSelectedItems({});
      setSelectedProcessName('');
      fetchPooledJobs();
      fetchPoolableProcesses();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to create pooled job');
    }
  };

  const toggleExpand = (jobId) => {
    setExpandedJob(expandedJob === jobId ? null : jobId);
  };

  const openReceiveDialog = (job) => {
    const form = {};
    job.line_items?.forEach(li => {
      form[`${li.work_order_id}_${li.process_id}`] = {
        completed_qty: li.quantity_to_produce - (li.quantity_completed || 0),
        defect_qty: 0
      };
    });
    setReceiveForm(form);
    setReceiveDialog({ open: true, job });
  };

  const handleReceive = async () => {
    const job = receiveDialog.job;
    if (!job) return;

    const line_item_completions = [];
    const defect_quantities = [];

    job.line_items?.forEach(li => {
      const key = `${li.work_order_id}_${li.process_id}`;
      const data = receiveForm[key];
      if (data && data.completed_qty > 0) {
        line_item_completions.push({
          work_order_id: li.work_order_id,
          process_id: li.process_id,
          completed_qty: parseFloat(data.completed_qty) || 0
        });
        if (data.defect_qty > 0) {
          defect_quantities.push({
            work_order_id: li.work_order_id,
            process_id: li.process_id,
            defect_qty: parseFloat(data.defect_qty) || 0
          });
        }
      }
    });

    try {
      await axios.post(`${API_URL}/pooled-jobs/${job.id}/receive`, {
        line_item_completions,
        defect_quantities,
        actual_time_hours: 0,
        quality_passed: true
      }, { headers: getAuthHeader() });

      toast.success('Production received successfully');
      setReceiveDialog({ open: false, job: null });
      fetchPooledJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to receive production');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  const JobCard = ({ job }) => {
    const isExpanded = expandedJob === job.id;
    const totalQty = job.line_items?.reduce((sum, li) => sum + (li.quantity_to_produce || 0), 0) || 0;
    const completedQty = job.line_items?.reduce((sum, li) => sum + (li.quantity_completed || 0), 0) || 0;
    const progress = totalQty > 0 ? Math.round((completedQty / totalQty) * 100) : 0;

    return (
      <Card className="mb-4" data-testid={`pooled-job-${job.pooled_job_number}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleExpand(job.id)}
                className="p-1"
              >
                {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-lg">{job.pooled_job_number}</span>
                  <Badge className={statusColors[job.status]}>{statusLabels[job.status]}</Badge>
                </div>
                <CardDescription>{job.process_name}</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Progress</p>
                <p className="font-semibold">{completedQty}/{totalQty} ({progress}%)</p>
              </div>
              {job.status !== 'completed' && (
                <Button
                  size="sm"
                  onClick={() => openReceiveDialog(job)}
                  data-testid={`receive-${job.pooled_job_number}`}
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Receive
                </Button>
              )}
            </div>
          </div>

          {isExpanded && (
            <div className="mt-4 border-t pt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-xs text-muted-foreground">Work Orders</p>
                  <p className="font-medium">{job.line_items?.length || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Weight</p>
                  <p className="font-medium">{formatWeight(job.total_weight || 0)} kg</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Machine</p>
                  <p className="font-medium">{job.machine || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Issued At</p>
                  <p className="font-medium">{job.issued_at ? formatDate(job.issued_at) : '-'}</p>
                </div>
              </div>

              {job.workers?.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-muted-foreground mb-2">Workers</p>
                  <div className="flex flex-wrap gap-2">
                    {job.workers.map((w, i) => (
                      <Badge key={i} variant="outline">
                        <Users className="w-3 h-3 mr-1" />
                        {w.employee_name} ({w.role})
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>WO Number</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Completed</TableHead>
                    <TableHead className="text-right">Weight %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {job.line_items?.map((li, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{li.work_order_number}</TableCell>
                      <TableCell>{li.item_name}</TableCell>
                      <TableCell className="text-right">{li.quantity_to_produce} {li.unit}</TableCell>
                      <TableCell className="text-right">{li.quantity_completed || 0} {li.unit}</TableCell>
                      <TableCell className="text-right">{formatPercentage(li.weight_percentage || 0)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {job.combined_materials?.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Scale className="w-4 h-4" />
                    Combined Materials
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {job.combined_materials.map((mat, idx) => (
                      <div key={idx} className="p-2 bg-muted rounded text-sm">
                        <p className="font-medium truncate">{mat.item_name}</p>
                        <p className="text-muted-foreground">
                          {formatBomQty(mat.total_quantity)} {mat.unit}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {job.remarks && (
                <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Remarks</p>
                  <p className="text-sm">{job.remarks}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Layers className="w-6 h-6 text-purple-600" />
              Pooled Job Cards
            </h1>
            <p className="text-muted-foreground">
              Create and manage pooled job cards that combine multiple work orders
            </p>
          </div>
          <Button onClick={() => { fetchPooledJobs(); fetchPoolableProcesses(); }} variant="outline" data-testid="refresh-pooled-jobs">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* CREATE POOLED JOB SECTION */}
        <Card id="create-section" className="border-purple-200 bg-purple-50/30">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Plus className="w-5 h-5 text-purple-600" />
                  Create Pooled Job
                </CardTitle>
                <CardDescription>
                  Select a process and choose which work orders to combine
                </CardDescription>
              </div>
              <Button 
                onClick={openCreateDialog}
                disabled={selectedCount === 0}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="create-pooled-job-top-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Pooled Job
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Process Dropdown */}
            <div className="flex items-center gap-4">
              <div className="w-64">
                <Label className="text-xs text-muted-foreground mb-1 block">Select Process</Label>
                <Select value={selectedProcessName} onValueChange={handleProcessChange}>
                  <SelectTrigger data-testid="process-select">
                    <SelectValue placeholder="Choose a process..." />
                  </SelectTrigger>
                  <SelectContent>
                    {poolableData.distinct_processes.map(proc => (
                      <SelectItem key={proc} value={proc}>{proc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedProcessName && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-btn">Select All</Button>
                  <Button variant="outline" size="sm" onClick={handleClearSelection} data-testid="clear-selection-btn">Clear</Button>
                </div>
              )}
            </div>
            
            {/* Material Requirements Info - Sticky Header */}
            {firstSelectedMaterials && firstSelectedMaterials.names?.length > 0 && (
              <div className="text-sm bg-amber-50 border border-amber-200 p-3 rounded-lg flex items-start gap-2" data-testid="material-requirements-banner">
                <Package className="w-5 h-5 mt-0.5 text-amber-600 flex-shrink-0" />
                <div>
                  <span className="font-medium text-amber-800">Required materials for pooling: </span>
                  <span className="text-amber-700">{firstSelectedMaterials.names.join(', ')}</span>
                  <p className="text-xs text-amber-600 mt-1">
                    Only processes with these exact materials can be selected. Items with different materials show "Mismatch" status.
                  </p>
                </div>
              </div>
            )}
            
            {/* Cumulative Material Requirements Summary - At Top for visibility */}
            {Object.keys(cumulativeMaterials).length > 0 && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="cumulative-materials-summary">
                <h5 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Cumulative Material Requirements
                </h5>
                <div className="space-y-1">
                  {Object.entries(cumulativeMaterials).map(([itemId, mat]) => {
                    const isOverLimit = mat.required_qty > mat.available_qty;
                    const percentage = mat.available_qty > 0 ? (mat.required_qty / mat.available_qty * 100) : 100;
                    return (
                      <div key={itemId} className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">{mat.item_name}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${isOverLimit ? 'bg-red-500' : percentage > 80 ? 'bg-amber-500' : 'bg-green-500'}`}
                              style={{ width: `${Math.min(percentage, 100)}%` }}
                            />
                          </div>
                          <span className={`font-medium ${isOverLimit ? 'text-red-600' : 'text-slate-700'}`}>
                            {mat.required_qty.toFixed(1)} / {mat.available_qty.toFixed(1)}
                          </span>
                          {isOverLimit && (
                            <span className="text-xs text-red-600 font-medium">(Over by {(mat.required_qty - mat.available_qty).toFixed(1)})</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Poolable Items Table */}
            {selectedProcessName && (
              <>
                {loadingPoolable ? (
                  <div className="text-center py-8 text-muted-foreground">Loading...</div>
                ) : filteredPoolableItems.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Package className="w-10 h-10 mx-auto mb-2 opacity-40" />
                    <p>No poolable processes found for "{selectedProcessName}"</p>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden bg-white">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/40">
                          <TableHead className="w-10"></TableHead>
                          <TableHead>WO Number</TableHead>
                          <TableHead>Item</TableHead>
                          <TableHead>SO Number</TableHead>
                          <TableHead>Delivery Date</TableHead>
                          <TableHead className="text-right">WO Qty</TableHead>
                          <TableHead className="text-right">Issued</TableHead>
                          <TableHead className="text-right">Pending</TableHead>
                          <TableHead className="text-center">Materials</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredPoolableItems.map((item, idx) => {
                          const key = `${item.work_order_id}-${item.process_id}`;
                          const isSelected = !!selectedItems[key];
                          const matConfig = materialStatusConfig[item.material_status] || materialStatusConfig.none;
                          const materialMismatch = hasMaterialMismatch(item);
                          const stockExceeded = !isSelected ? wouldExceedStock(item) : null;  // Only check for unselected items
                          const canSelect = item.material_status === 'ready' && !materialMismatch && !stockExceeded;
                          
                          // Determine tooltip message
                          let disabledReason = '';
                          if (item.material_status !== 'ready') {
                            disabledReason = 'Insufficient stock — cannot select';
                          } else if (materialMismatch) {
                            disabledReason = `Different materials — cannot pool with selected processes (requires: ${firstSelectedMaterials?.names?.join(', ') || 'none'})`;
                          } else if (stockExceeded) {
                            disabledReason = `Insufficient stock — selecting would require ${stockExceeded.would_need.toFixed(1)} ${stockExceeded.item_name}, only ${stockExceeded.available.toFixed(1)} available`;
                          }
                          
                          return (
                            <TableRow 
                              key={`${key}-${isSelected}`} 
                              className={`transition-colors ${canSelect ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'} ${isSelected ? 'bg-purple-50' : canSelect ? 'hover:bg-muted/30' : ''} ${materialMismatch ? 'bg-orange-50/30' : ''} ${stockExceeded ? 'bg-red-50/30' : ''}`}
                              onClick={() => canSelect && toggleItemSelection(item)}
                              data-testid={`poolable-row-${idx}`}
                              title={disabledReason}
                            >
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <Checkbox 
                                  checked={isSelected}
                                  disabled={!canSelect && !isSelected}
                                  onCheckedChange={() => (canSelect || isSelected) && toggleItemSelection(item)}
                                  data-testid={`poolable-checkbox-${idx}`}
                                  title={disabledReason}
                                />
                              </TableCell>
                              <TableCell className="font-medium">{item.work_order_number}</TableCell>
                              <TableCell>
                                <div>
                                  <p className="text-sm">{item.item_name}</p>
                                  {item.item_code && <p className="text-xs text-muted-foreground">{item.item_code}</p>}
                                </div>
                              </TableCell>
                              <TableCell>{item.so_number || '-'}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3 text-muted-foreground" />
                                  <span className="text-sm">{formatDate(item.delivery_date)}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-right">{item.wo_quantity}</TableCell>
                              <TableCell className="text-right text-muted-foreground">{item.already_issued}</TableCell>
                              <TableCell className="text-right font-semibold">{item.pending_qty}</TableCell>
                              <TableCell className="text-center">
                                <div className="flex items-center justify-center gap-1.5" title={disabledReason || matConfig.label}>
                                  <span className={`w-2.5 h-2.5 rounded-full ${stockExceeded ? 'bg-red-500' : materialMismatch ? 'bg-orange-500' : matConfig.color}`}></span>
                                  <span className={`text-xs ${stockExceeded ? 'text-red-700' : materialMismatch ? 'text-orange-700' : matConfig.textColor}`}>
                                    {stockExceeded ? 'Stock Limit' : materialMismatch ? 'Mismatch' : matConfig.label}
                                  </span>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Selection Summary & Create Button */}
                {filteredPoolableItems.length > 0 && (
                  <div className="space-y-2 pt-2 border-t">
                    {/* Material requirement info - Bottom (visible when scrolled) */}
                    {firstSelectedMaterials && firstSelectedMaterials.names?.length > 0 && (
                      <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded flex items-start gap-2">
                        <Package className="w-4 h-4 mt-0.5 text-purple-600 flex-shrink-0" />
                        <span>
                          <strong>Required materials for pooling:</strong> {firstSelectedMaterials.names.join(', ')}
                          <span className="text-orange-600 ml-2">(Only processes with matching materials can be selected)</span>
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="text-sm">
                        {selectedCount > 0 ? (
                          <span className="font-medium text-purple-700">
                            Selected: <strong>{selectedCount}</strong> process{selectedCount !== 1 ? 'es' : ''} • <strong>{selectedTotalQty}</strong> units
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Select processes to create a pooled job</span>
                        )}
                      </div>
                      <Button 
                        onClick={openCreateDialog} 
                        disabled={selectedCount === 0}
                        className="bg-purple-600 hover:bg-purple-700"
                        data-testid="open-create-pooled-dialog"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Create Pooled Job
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* EXISTING POOLED JOBS SECTION */}
        <div className="border-t pt-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Existing Pooled Jobs
          </h2>

          {/* Stats Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Jobs</p>
                    <p className="text-2xl font-bold">{pooledJobs.length}</p>
                  </div>
                  <Layers className="w-8 h-8 text-blue-200" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Active</p>
                    <p className="text-2xl font-bold text-yellow-600">{activeJobs.length}</p>
                  </div>
                  <Clock className="w-8 h-8 text-yellow-200" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Completed</p>
                    <p className="text-2xl font-bold text-green-600">{completedJobs.length}</p>
                  </div>
                  <CheckCircle2 className="w-8 h-8 text-green-200" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Work Orders</p>
                    <p className="text-2xl font-bold">
                      {pooledJobs.reduce((sum, j) => sum + (j.line_items?.length || 0), 0)}
                    </p>
                  </div>
                  <Package className="w-8 h-8 text-purple-200" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="active">Active ({activeJobs.length})</TabsTrigger>
              <TabsTrigger value="completed">Completed ({completedJobs.length})</TabsTrigger>
              <TabsTrigger value="all">All ({pooledJobs.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="active" className="mt-4">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : activeJobs.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Layers className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No active pooled jobs</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Use the section above to create a new pooled job
                    </p>
                  </CardContent>
                </Card>
              ) : (
                activeJobs.map(job => <JobCard key={job.id} job={job} />)
              )}
            </TabsContent>

            <TabsContent value="completed" className="mt-4">
              {completedJobs.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <CheckCircle2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No completed pooled jobs yet</p>
                  </CardContent>
                </Card>
              ) : (
                completedJobs.map(job => <JobCard key={job.id} job={job} />)
              )}
            </TabsContent>

            <TabsContent value="all" className="mt-4">
              {pooledJobs.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Layers className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No pooled jobs found</p>
                  </CardContent>
                </Card>
              ) : (
                pooledJobs.map(job => <JobCard key={job.id} job={job} />)
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Create Pooled Job Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-purple-600" />
                Create Pooled Job
              </DialogTitle>
              <DialogDescription>
                {selectedCount} process{selectedCount !== 1 ? 'es' : ''} selected • {selectedTotalQty} total units • Process: {selectedProcessName}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Workers */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold">Workers</Label>
                  {createForm.workers.length < 5 && (
                    <Button type="button" size="sm" variant="outline" onClick={addWorker}>
                      <Plus className="w-3 h-3 mr-1" /> Add Worker
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  {createForm.workers.map((w, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <SearchableSelect
                        options={employees.map(e => ({ value: e.id, label: e.name }))}
                        value={w.employee_id}
                        onValueChange={(v) => updateWorker(idx, 'employee_id', v)}
                        placeholder="Select worker"
                        className="flex-1"
                      />
                      <Select value={w.role} onValueChange={(v) => updateWorker(idx, 'role', v)}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="operator">Operator</SelectItem>
                          <SelectItem value="helper">Helper</SelectItem>
                          <SelectItem value="supervisor">Supervisor</SelectItem>
                          <SelectItem value="technician">Technician</SelectItem>
                        </SelectContent>
                      </Select>
                      {createForm.workers.length > 1 && (
                        <Button type="button" size="sm" variant="ghost" onClick={() => removeWorker(idx)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Machine */}
              <div className="space-y-2">
                <Label>Machine</Label>
                <Input
                  value={createForm.machine}
                  onChange={(e) => setCreateForm({ ...createForm, machine: e.target.value })}
                  placeholder="e.g., Machine #1"
                />
              </div>

              {/* Drawing Number */}
              <div className="space-y-2">
                <Label>Drawing Number</Label>
                <Input
                  value={createForm.drawing_number}
                  onChange={(e) => setCreateForm({ ...createForm, drawing_number: e.target.value })}
                  placeholder="Optional"
                />
              </div>

              {/* Remarks */}
              <div className="space-y-2">
                <Label>Remarks</Label>
                <Input
                  value={createForm.remarks}
                  onChange={(e) => setCreateForm({ ...createForm, remarks: e.target.value })}
                  placeholder="Optional notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreatePooledJob} className="bg-purple-600 hover:bg-purple-700" data-testid="confirm-create-pooled">
                <Layers className="w-4 h-4 mr-2" />
                Create Pooled Job
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Receive Dialog */}
        <Dialog open={receiveDialog.open} onOpenChange={(open) => !open && setReceiveDialog({ open: false, job: null })}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Receive Production - {receiveDialog.job?.pooled_job_number}</DialogTitle>
              <DialogDescription>
                Enter completed quantities for each work order in this pooled job
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-96 overflow-y-auto">
              {receiveDialog.job?.line_items?.map((li, idx) => {
                const key = `${li.work_order_id}_${li.process_id}`;
                const pending = li.quantity_to_produce - (li.quantity_completed || 0);
                return (
                  <div key={idx} className="p-4 border rounded-lg">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-semibold">{li.work_order_number}</p>
                        <p className="text-sm text-muted-foreground">{li.item_name}</p>
                      </div>
                      <Badge variant="outline">Pending: {pending} {li.unit}</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Completed Qty</Label>
                        <Input
                          type="number"
                          value={receiveForm[key]?.completed_qty || 0}
                          onChange={(e) => setReceiveForm({
                            ...receiveForm,
                            [key]: { ...receiveForm[key], completed_qty: e.target.value }
                          })}
                          max={pending}
                          data-testid={`receive-qty-${idx}`}
                        />
                      </div>
                      <div>
                        <Label>Defect Qty</Label>
                        <Input
                          type="number"
                          value={receiveForm[key]?.defect_qty || 0}
                          onChange={(e) => setReceiveForm({
                            ...receiveForm,
                            [key]: { ...receiveForm[key], defect_qty: e.target.value }
                          })}
                          data-testid={`defect-qty-${idx}`}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReceiveDialog({ open: false, job: null })}>
                Cancel
              </Button>
              <Button onClick={handleReceive} data-testid="confirm-receive">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Receive Production
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
